import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { TextArea } from '../ui/Input';
import { Select } from '../ui/Select';
import { praiseAPI, organizationAPI } from '../../services/api';
import { useOrganization } from '../../contexts/OrganizationContext';
import { showSuccess, showError } from '../../utils/toast';
import { unwrapResponse } from '../../utils/unwrapResponse';

interface QuickPraiseProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Member {
  id: string;
  displayName: string;
}

export function QuickPraise({ isOpen, onClose }: QuickPraiseProps) {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const [members, setMembers] = useState<Member[]>([]);
  const [recipientId, setRecipientId] = useState('');
  const [message, setMessage] = useState('');
  const [points, setPoints] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch members when sheet opens
  useEffect(() => {
    if (!isOpen || !organization) return;
    let cancelled = false;
    organizationAPI.getMembersOnly(String(organization.id))
      .then(res => {
        if (cancelled) return;
        const unwrapped = unwrapResponse<any>(res.data);
        const memberList = (unwrapped?.members || (Array.isArray(unwrapped) ? unwrapped : [])) as Array<{
          userId?: string;
          id?: string;
          displayName?: string;
          userName?: string;
          name?: string;
          username?: string;
        }>;
        setMembers(
          memberList.map(m => ({
            id: String(m.userId || m.id),
            displayName: m.displayName || m.userName || m.name || m.username || 'Member',
          }))
        );
      })
      .catch(() => {
        // Silently fail; user can still type
      });
    return () => { cancelled = true; };
  }, [isOpen, organization]);

  const resetForm = useCallback(() => {
    setRecipientId('');
    setMessage('');
    setPoints('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!recipientId || !message.trim()) return;

    setSubmitting(true);
    try {
      const payload: {
        recipientId: string;
        message: string;
        awardPoints?: boolean;
        points?: number;
      } = {
        recipientId,
        message: message.trim(),
      };

      const parsedPoints = parseInt(points, 10);
      if (!isNaN(parsedPoints) && parsedPoints > 0) {
        payload.awardPoints = true;
        payload.points = parsedPoints;
      }

      await praiseAPI.send(payload);
      queryClient.invalidateQueries({ queryKey: ['praise'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      showSuccess('Praise sent!');
      resetForm();
      onClose();
    } catch (_err) {
      showError('Failed to send praise');
    } finally {
      setSubmitting(false);
    }
  }, [recipientId, message, points, queryClient, resetForm, onClose]);

  return (
    <Sheet open={isOpen} onDismiss={handleClose} title="Quick Praise">
      <div className="flex flex-col gap-4">
        {/* Partner selector */}
        <Select
          id="praise-recipient"
          value={recipientId}
          onChange={e => setRecipientId(e.target.value)}
          size="md"
          label="Send to"
        >
          <option value="">Select a member...</option>
          {members.map(m => (
            <option key={m.id} value={m.id}>
              {m.displayName}
            </option>
          ))}
        </Select>

        <TextArea
          label="Message"
          placeholder="What are you praising them for?"
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={3}
          required
        />

        {/* Points (optional) */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="praise-points" className="text-sm font-medium text-text-secondary">
            Points (optional)
          </label>
          <input
            id="praise-points"
            type="number"
            min="0"
            placeholder="0"
            value={points}
            onChange={e => setPoints(e.target.value)}
            className="h-11 px-3 w-full bg-surface border border-border-light rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
          />
        </div>

        <Button
          variant="primary"
          fullWidth
          loading={submitting}
          disabled={!recipientId || !message.trim() || submitting}
          onClick={handleSubmit}
        >
          Send Praise
        </Button>
      </div>
    </Sheet>
  );
}
